import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import Swal from "sweetalert2";
import { AssetFinanceUrl } from "../../../../../config";
import { fetchJsonDynamic } from "../../../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = AssetFinanceUrl.uri;
const FILE_GATEWAY_URL = "https://api.sheriapro.sababisha.com";
const FILE_STORAGE_API_KEY =
  "E3VF4C6KFV43JH6GKCDFGJP45V36JHGV3H4C6F3GJC63HG405GH6V345GHHJ4623FJJL3HCVMO1P23PZ07W8";

const parseResults = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (typeof response?.results === "string") {
    try {
      const parsed = JSON.parse(response.results);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getFileIcon = (mimeType, fileName) => {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (mimeType?.includes("pdf") || ext === "pdf")
    return <PictureAsPdfOutlinedIcon sx={{ fontSize: 26, color: "#ef4444" }} />;
  if (
    mimeType?.startsWith("image/") ||
    ["jpg", "jpeg", "png", "webp", "gif"].includes(ext)
  )
    return <ImageOutlinedIcon sx={{ fontSize: 26, color: "#3b82f6" }} />;
  return <ArticleOutlinedIcon sx={{ fontSize: 26, color: "#6366f1" }} />;
};

const DOC_COLORS = [
  { bg: "#eff6ff", border: "#bfdbfe", icon: "#1d4ed8" },
  { bg: "#f0fdf4", border: "#bbf7d0", icon: "#15803d" },
  { bg: "#fdf4ff", border: "#e9d5ff", icon: "#7e22ce" },
  { bg: "#fff7ed", border: "#fed7aa", icon: "#c2410c" },
  { bg: "#f0fdfa", border: "#99f6e4", icon: "#0f766e" },
  { bg: "#fefce8", border: "#fde68a", icon: "#b45309" },
];

const ViewCollateralDocumentsModal = ({ open, onClose, asset }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingDocId, setOpeningDocId] = useState(null);

  useEffect(() => {
    if (!open || !asset?.Id) return;
    setDocuments([]);
    (async () => {
      setLoading(true);
      try {
        const response = await fetchJsonDynamic(
          "collateralAssetDocumentByColumnValues",
          "collateralDocument",
          { collateralAssetId: Number(asset.Id) },
          base_url,
        );
        setDocuments(parseResults(response));
      } catch (error) {
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, asset?.Id]);

  const handleOpenDocument = async (doc) => {
    if (!doc.ObjectName && !doc.Url) {
      Swal.fire(
        "Error",
        "No valid storage path found for this document.",
        "error",
      );
      return;
    }
    setOpeningDocId(doc.Id);
    try {
      let documentUrl = doc.Url;
      if (doc.ObjectName) {
        const res = await fetch(
          `${FILE_GATEWAY_URL}/filestorageservice/presigned-url?objectName=${encodeURIComponent(doc.ObjectName)}&expiry=3600`,
          { method: "GET", headers: { "X-Api-Key": FILE_STORAGE_API_KEY } },
        );
        if (res.ok) {
          const data = await res.json();
          if (data?.url) documentUrl = data.url;
        }
      }
      if (documentUrl) {
        window.open(documentUrl, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Could not retrieve presigned document URL");
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Could not open document",
        text: "Please check your network connection and try again.",
      });
    } finally {
      setOpeningDocId(null);
    }
  };

  const assetLabel = [asset?.AssetTypeName, asset?.SubTypeName]
    .filter(Boolean)
    .join(" › ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          bgcolor: isDark ? "#111827" : "#f8fafc",
          boxShadow: isDark
            ? "0 32px 80px rgba(0,0,0,0.7)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          mx: "auto",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiBackdrop-root": {
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0,0,0,0.45)",
        },
      }}
    >
      {/* ── Gradient Header ── */}
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #1e3a5f 0%, #0f2d4a 100%)"
            : "linear-gradient(135deg, #1766a2 0%, #2196f3 100%)",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            bgcolor: "rgba(255,255,255,0.1)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FolderOpenOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={800}
              color="#fff"
              lineHeight={1.2}
            >
              Collateral Documents
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.72)",
                mt: 0.2,
                display: "block",
              }}
            >
              Asset #{asset?.Id}
              {assetLabel ? ` · ${assetLabel}` : ""}
            </Typography>
          </Box>
          {!loading && (
            <Chip
              label={`${documents.length} file${documents.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                ml: "auto",
                mr: 4,
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.72rem",
                height: 24,
                borderRadius: "8px",
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Content ── */}
      <DialogContent
        sx={{
          px: 2.5,
          py: 2.5,
          bgcolor: isDark ? "#111827" : "#f8fafc",
          minHeight: 180,
        }}
      >
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            gap={2}
          >
            <CircularProgress size={36} thickness={4} />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Fetching documents...
            </Typography>
          </Box>
        ) : documents.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            gap={1.5}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DescriptionOutlinedIcon
                sx={{
                  fontSize: 32,
                  color: isDark ? "rgba(255,255,255,0.3)" : "#94a3b8",
                }}
              />
            </Box>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              No documents attached
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              maxWidth={280}
            >
              No supporting documents have been uploaded for this collateral
              asset yet.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {documents.map((doc, index) => {
              const isOpening = openingDocId === doc.Id;
              const fileSizeStr = formatFileSize(doc.FileSizeBytes);
              const color = isDark
                ? null
                : DOC_COLORS[index % DOC_COLORS.length];
              const dateStr = doc.CreatedAt
                ? new Date(doc.CreatedAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : null;

              return (
                <Box
                  key={doc.Id || doc.ObjectName}
                  onClick={() => !isOpening && handleOpenDocument(doc)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1.75,
                    borderRadius: "14px",
                    border: `1.5px solid ${isDark ? "rgba(255,255,255,0.09)" : color?.border || "#e2e8f0"}`,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.04)"
                      : color?.bg || "#fff",
                    cursor: isOpening ? "default" : "pointer",
                    transition: "all 0.18s ease",
                    "&:hover": isOpening
                      ? {}
                      : {
                          transform: "translateY(-2px)",
                          boxShadow: isDark
                            ? "0 6px 20px rgba(0,0,0,0.35)"
                            : "0 6px 20px rgba(0,0,0,0.08)",
                          borderColor: isDark
                            ? "rgba(255,255,255,0.2)"
                            : color?.icon
                              ? alpha(color.icon, 0.4)
                              : "#94a3b8",
                        },
                  }}
                >
                  {/* File Icon */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.08)"
                        : alpha(color?.icon || "#1766a2", 0.12),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {getFileIcon(doc.MimeType, doc.FileName)}
                  </Box>

                  {/* Doc Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: isDark ? "#e2e8f0" : "#0f172a",
                        fontSize: "0.88rem",
                      }}
                    >
                      {doc.DocumentName || "Collateral Document"}
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.75}
                      mt={0.4}
                      flexWrap="wrap"
                    >
                      {fileSizeStr && (
                        <Chip
                          label={fileSizeStr}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.62rem",
                            fontWeight: 600,
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.1)"
                              : alpha(color?.icon || "#1766a2", 0.12),
                            color: isDark
                              ? "rgba(255,255,255,0.6)"
                              : color?.icon || "#1766a2",
                            border: "none",
                          }}
                        />
                      )}
                      {dateStr && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          · {dateStr}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* View Button */}
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={
                      isOpening ? (
                        <CircularProgress size={13} color="inherit" />
                      ) : (
                        <OpenInNewIcon sx={{ fontSize: "15px !important" }} />
                      )
                    }
                    disabled={isOpening}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDocument(doc);
                    }}
                    sx={{
                      flexShrink: 0,
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      px: 1.8,
                      py: 0.7,
                      whiteSpace: "nowrap",
                      background: isDark
                        ? "linear-gradient(135deg, #1766a2, #2196f3)"
                        : `linear-gradient(135deg, ${color?.icon || "#1766a2"}, ${alpha(color?.icon || "#1766a2", 0.75)})`,
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: `0 4px 12px ${alpha(color?.icon || "#1766a2", 0.4)}`,
                        transform: "scale(1.03)",
                      },
                      transition: "all 0.18s ease",
                    }}
                  >
                    {isOpening ? "Opening…" : "Open"}
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      {!loading && documents.length > 0 && (
        <>
          <Divider
            sx={{ borderColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0" }}
          />
          <DialogActions
            sx={{ px: 3, py: 1.75, bgcolor: isDark ? "#111827" : "#f8fafc" }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              Click any row or "Open" to view the document in a new tab.
            </Typography>
            <Button
              onClick={onClose}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                color: isDark ? "rgba(255,255,255,0.7)" : "#475569",
                "&:hover": {
                  borderColor: isDark ? "rgba(255,255,255,0.4)" : "#94a3b8",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ViewCollateralDocumentsModal;
